import React, { useEffect, useRef } from 'react'
import { getWordAtCell } from '../../utils/gridUtils'

const styles = {
  container: {
    outline: 'none',
    position: 'relative',
  },
  pauseOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(255, 255, 255, 0.9)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10,
    fontSize: '1.25rem',
    fontWeight: '600',
    color: 'var(--color-muted)',
  },
  grid: {
    display: 'inline-block',
    border: '2px solid var(--color-primary)',
  },
  row: {
    display: 'flex',
  },
  cell: {
    width: '36px',
    height: '36px',
    border: '1px solid var(--color-primary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    position: 'relative',
    background: '#fff',
    fontSize: '18px',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  cellBlack: {
    background: 'var(--color-primary)',
    cursor: 'default',
  },
  cellSelected: {
    background: 'var(--color-warning)',
  },
  cellHighlighted: {
    background: '#e6f3ff',
  },
  cellCorrect: {
    background: '#d4edda',
  },
  cellIncorrect: {
    background: '#f8d7da',
  },
  cellRevealed: {
    color: 'var(--color-muted)',
  },
  cellNumber: {
    position: 'absolute',
    top: '1px',
    left: '2px',
    fontSize: '10px',
    fontWeight: '700',
    lineHeight: 1,
  },
}

export default function SolverGrid({
  grid,
  userGrid,
  numberedCells,
  selectedCell,
  setSelectedCell,
  direction,
  setDirection,
  onKeyDown,
  cellStates = {},
  isPaused = false,
}) {
  const containerRef = useRef(null)

  useEffect(() => {
    containerRef.current?.focus()
  }, [])

  if (!grid) return null

  // Get highlighted cells (the current word)
  const highlightedCells = new Set()
  if (selectedCell && !grid[selectedCell.row][selectedCell.col].isBlack) {
    const word = getWordAtCell(grid, selectedCell.row, selectedCell.col, direction)
    if (word) {
      word.cells.forEach(c => highlightedCells.add(`${c.row}-${c.col}`))
    }
  }

  const handleCellClick = (row, col) => {
    if (grid[row][col].isBlack) return

    if (selectedCell?.row === row && selectedCell?.col === col) {
      // Toggle direction on re-click
      setDirection(d => d === 'across' ? 'down' : 'across')
    } else {
      setSelectedCell({ row, col })
    }
  }

  return (
    <div
      ref={containerRef}
      style={styles.container}
      tabIndex={0}
      onKeyDown={isPaused ? undefined : onKeyDown}
    >
      {isPaused && (
        <div style={styles.pauseOverlay}>
          Paused
        </div>
      )}
      <div style={{ ...styles.grid, filter: isPaused ? 'blur(8px)' : 'none' }}>
        {grid.map((row, rowIdx) => (
          <div key={rowIdx} style={styles.row}>
            {row.map((cell, colIdx) => {
              const key = `${rowIdx}-${colIdx}`
              const isSelected = selectedCell?.row === rowIdx && selectedCell?.col === colIdx
              const isHighlighted = highlightedCells.has(key) && !isSelected
              const cellState = cellStates[key]
              const cellNum = numberedCells.cells[key]
              const userLetter = userGrid?.[rowIdx]?.[colIdx]?.letter || ''

              let cellStyle = { ...styles.cell }
              if (cell.isBlack) {
                cellStyle = { ...cellStyle, ...styles.cellBlack }
              } else if (isSelected) {
                cellStyle = { ...cellStyle, ...styles.cellSelected }
              } else if (isHighlighted) {
                cellStyle = { ...cellStyle, ...styles.cellHighlighted }
              }

              if (cellState === 'correct') {
                cellStyle = { ...cellStyle, ...styles.cellCorrect }
              } else if (cellState === 'incorrect') {
                cellStyle = { ...cellStyle, ...styles.cellIncorrect }
              } else if (cellState === 'revealed') {
                cellStyle = { ...cellStyle, ...styles.cellRevealed }
              }

              return (
                <div
                  key={colIdx}
                  style={cellStyle}
                  onClick={() => handleCellClick(rowIdx, colIdx)}
                >
                  {cellNum && !cell.isBlack && (
                    <span style={styles.cellNumber}>{cellNum}</span>
                  )}
                  {!cell.isBlack && userLetter}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
